// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, Observable } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { showSnackBar } from "../../snackbar/actions"
import { assistantService, Code, executor, Init, isConnectError } from "../../../app/rpc"
import {
  BatchTranslateTextsRequestSchema,
  BatchTranslateTextsResponse,
} from "../../../generated/wisetime/user/assistant/v4/assistant_pb"

type TranslateNarrativesRpc = (request: Init<typeof BatchTranslateTextsRequestSchema>) => Observable<BatchTranslateTextsResponse>

export const translateNarratives = (translateNarrativesRpc: TranslateNarrativesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.translateNarratives.match),
      mergeMap(action => translateNarrativesRpc(action.payload)
        .pipe(
          map(response => response.translations),
          map(TimelineAdditionalUiSlice.setPendingNarratives),
          catchError(e => handleError(e, action.payload)),
        )
      ),
    )

const handleError = (e: unknown, request: Init<typeof BatchTranslateTextsRequestSchema>): Observable<Action> => {
  const actions: Action[] = [
    TimelineAdditionalUiSlice.deletePendingNarratives((request.translations ?? []).map(t => t.key ?? "")),
  ]
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ id: "msg_translate_narratives_permission_denied", message: "Failed to translate narrative(s). You do not have permission." }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({ id: "msg_translate_narratives_failed", message: "Failed to translate narrative(s). Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const translateNarrativesRpc = (request: Init<typeof BatchTranslateTextsRequestSchema>): Observable<BatchTranslateTextsResponse> =>
  executor(() => assistantService.batchTranslateTexts(create(BatchTranslateTextsRequestSchema, request)))

export default translateNarratives(translateNarrativesRpc)
